import { frameCount, numberOfChannels, type PcmAudio } from "../../types";

export interface WavEncodeOptions {
  /** Bits per sample. 16-bit PCM is the safe, universal default. */
  bitDepth?: 16 | 32;
}

/**
 * Encode decoded PCM to a WAV (RIFF/PCM) file. Pure — no Web Audio.
 *
 * 16-bit: integer PCM (`format` 1). 32-bit: IEEE float (`format` 3), lossless
 * vs. the in-memory floats. Channels are interleaved per the WAV spec.
 */
export function encodeWav(audio: PcmAudio, options: WavEncodeOptions = {}): ArrayBuffer {
  const bitDepth = options.bitDepth ?? 16;
  const channels = numberOfChannels(audio);
  const frames = frameCount(audio);
  const bytesPerSample = bitDepth / 8;
  const isFloat = bitDepth === 32;

  const blockAlign = channels * bytesPerSample;
  const dataSize = frames * blockAlign;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  const writeString = (offset: number, text: string) => {
    for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i));
  };

  // RIFF header
  writeString(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  writeString(8, "WAVE");

  // fmt chunk
  writeString(12, "fmt ");
  view.setUint32(16, 16, true); // chunk size
  view.setUint16(20, isFloat ? 3 : 1, true); // audio format: 3 = float, 1 = PCM
  view.setUint16(22, channels, true);
  view.setUint32(24, audio.sampleRate, true);
  view.setUint32(28, audio.sampleRate * blockAlign, true); // byte rate
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitDepth, true);

  // data chunk
  writeString(36, "data");
  view.setUint32(40, dataSize, true);

  let offset = 44;
  for (let i = 0; i < frames; i++) {
    for (let c = 0; c < channels; c++) {
      const sample = audio.channelData[c]?.[i] ?? 0;
      if (isFloat) {
        view.setFloat32(offset, sample, true);
        offset += 4;
      } else {
        // Clamp to [-1, 1], then scale to signed 16-bit.
        const clamped = Math.max(-1, Math.min(1, sample));
        view.setInt16(offset, clamped < 0 ? clamped * 0x8000 : clamped * 0x7fff, true);
        offset += 2;
      }
    }
  }

  return buffer;
}
